import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Pickaxe, Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/24592751-840e-4937-8b48-1328a6b3de01/images/e35fde1d3c1f8d9a2d3660bdc5dd95f524bba8f7348054463852fb905e1f28a6.png";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Inserisci username e password");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await login(username.trim(), password);
      toast.success("Login effettuato");
      navigate("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Hero — desktop only */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden bg-[#09090B]"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(9,9,11,0.75), rgba(9,9,11,0.4)), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 grid-backdrop opacity-30" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#10B981] flex items-center justify-center">
              <Pickaxe className="w-5 h-5 text-[#09090B]" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black text-2xl tracking-tight">
              WART<span className="text-[#10B981]">HIVE</span>
            </div>
          </div>

          <div>
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#10B981] mb-4">
              // Mining Operations Console
            </div>
            <h1 className="font-heading font-black text-5xl xl:text-6xl tracking-tight leading-[1.05] max-w-xl">
              Comanda i tuoi rig.<br />
              <span className="text-[#10B981]">Domina l'hashrate.</span>
            </h1>
            <p className="mt-6 text-[#A1A1AA] max-w-md leading-relaxed">
              Dashboard centrale per 15 mining rig Ubuntu Server. Monitoring in
              tempo reale, overclock remoto, flight sheet e alert Telegram —
              tutto in un solo posto.
            </p>
          </div>

          <div className="flex items-center gap-8 font-mono text-xs text-[#A1A1AA] uppercase tracking-[0.18em]">
            <div>15 rigs</div>
            <div className="w-px h-3 bg-[#27272A]" />
            <div>NVIDIA · bzminer</div>
            <div className="w-px h-3 bg-[#27272A]" />
            <div>WART · Warthog</div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#09090B]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-9 h-9 rounded-sm bg-[#10B981] flex items-center justify-center">
              <Pickaxe className="w-4 h-4 text-[#09090B]" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black text-xl tracking-tight">
              WART<span className="text-[#10B981]">HIVE</span>
            </div>
          </div>

          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#10B981] mb-3">
            // Admin Access
          </div>
          <h2 className="font-heading font-bold text-3xl tracking-tight mb-2">
            Accedi alla console
          </h2>
          <p className="text-sm text-[#A1A1AA] mb-10">
            Login amministratore richiesto per gestire i rig.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] block mb-2">
                Username
              </span>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <input
                  data-testid="login-username-input"
                  autoFocus
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  className="w-full bg-[#18181B] border border-[#27272A] rounded-sm pl-10 pr-3 py-2.5 text-sm font-mono outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/30 transition-colors"
                  autoComplete="username"
                />
              </div>
            </label>

            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] block mb-2">
                Password
              </span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#18181B] border border-[#27272A] rounded-sm pl-10 pr-10 py-2.5 text-sm font-mono outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/30 transition-colors"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  data-testid="login-toggle-pw"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#FAFAFA]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            {error && (
              <div
                data-testid="login-error"
                className="text-xs font-mono text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 disabled:cursor-not-allowed text-[#09090B] font-bold rounded-sm py-2.5 text-sm uppercase tracking-[0.15em] transition-colors active:scale-[0.98]"
            >
              {submitting ? "Verifico…" : "Entra nella console"}
            </button>
          </form>

          <div className="mt-8 text-xs font-mono text-[#A1A1AA]">
            Default: <span className="text-[#FAFAFA]">admin</span> /{" "}
            <span className="text-[#FAFAFA]">admin123</span> (cambia in Settings)
          </div>
        </div>
      </div>
    </div>
  );
}
